import { useEffect, useId } from "react";
import { Handshake, Landmark, FileText, ListChecks, Megaphone, Settings, LayoutDashboard } from "lucide-react";

/** iter445 — REGISTRE COMMUN des modifications non enregistrées (règle permanente) : chaque formulaire s'inscrit quand il diffère de l'état
 *  enregistré ; `requestLeave` ouvre la fenêtre commune DeskLeaveDialog seulement si le registre n'est pas vide. L'écouteur `beforeunload`
 *  (seule exception navigateur autorisée) est attaché ICI uniquement, et seulement tant que le registre n'est pas vide. */
const entries = new Map();
const subs = new Set();
let request = null;
let skip = false;
let unloadAttached = false;

const onUnload = (e) => { if (skip) return; e.preventDefault(); e.returnValue = ""; };
const syncUnload = () => {
  if (typeof window === "undefined") return;
  if (entries.size > 0 && !unloadAttached) { window.addEventListener("beforeunload", onUnload); unloadAttached = true; }
  if (entries.size === 0 && unloadAttached) { window.removeEventListener("beforeunload", onUnload); unloadAttached = false; }
};
const emit = () => { syncUnload(); subs.forEach((cb) => cb()); };

export const skipNextUnloadPrompt = () => { skip = true; };
export const MODULES = {
  crm: { icon: Handshake, color: "#7C5CE0", label: "Commercial" }, tresorerie: { icon: Landmark, color: "#1D9E75", label: "Trésorerie" }, documents: { icon: FileText, color: "#3B9EFF", label: "Documents" },
  taches: { icon: ListChecks, color: "#E0A42E", label: "Tâches" }, avi: { icon: Megaphone, color: "#C2508A", label: "AVI" }, parametres: { icon: Settings, color: "#8A8A93", label: "Paramètres" }, default: { icon: LayoutDashboard, color: "#93B4F5", label: "Le Desk" },
};
export const moduleKeyOfPath = (p = window.location.pathname) => { const k = (p.split("/")[1] || "").replace("commercial", "crm"); return MODULES[k] ? k : "default"; };

export function registerLeave(id, entry) { entries.set(id, { ...(entries.get(id) || {}), ...entry, id }); emit(); }
export function unregisterLeave(id) { if (entries.delete(id)) emit(); }
export const hasLeaveEntry = (id) => entries.has(id);
export const leaveEntries = () => [...entries.values()];
export const hasLeaveEntries = () => entries.size > 0;
export function clearLeaveEntries() { entries.clear(); emit(); }
export function subscribeLeave(cb) { subs.add(cb); return () => subs.delete(cb); }
export const currentLeaveRequest = () => request;
export function resolveLeaveRequest() { request = null; emit(); }

/** Déclencheur : si rien n'est à perdre → `proceed()` immédiatement (aucune fenêtre) ; sinon fenêtre commune. kind = "desk" | "page". */
export function requestLeave(kind, proceed) {
  if (entries.size === 0) { proceed(); return true; }
  if (request) { // demandes successives dans le même geste (ex. fermeture du dialogue puis navigation) : enchaînées
    const prev = request.proceed;
    request = { kind: kind === "desk" || request.kind === "desk" ? "desk" : "page", proceed: () => { prev(); proceed(); } };
    emit();
    return false;
  }
  request = { kind, proceed };
  emit();
  return false;
}

/** Inscription explicite d'un formulaire : { dirty, label, module, state, save (→ true si enregistré), discard }. */
export function useLeaveGuard({ dirty, label, module = "default", state, save, discard, wizard = null }) {
  const id = useId();
  useEffect(() => {
    if (dirty) registerLeave(id, { label, module, state: state || "saisie en cours", save, discard, wizard });
    else unregisterLeave(id);
  }, [dirty, label, module, state, save, discard, wizard, id]);
  useEffect(() => () => unregisterLeave(id), [id]);
  return id;
}

const errCount = () => document.querySelectorAll('[data-testid="toast-error"]').length;
/** Enveloppe une fonction d'enregistrement : succès = pas d'exception et aucun nouveau toast d'erreur. */
export const guardedSave = (fn) => async () => {
  const n = errCount();
  try { await fn(); } catch { return false; }
  await new Promise((r) => setTimeout(r, 120));
  return errCount() === n;
};
/** Enregistrement par le bouton principal d'un conteneur DOM : succès = l'inscription `id` disparaît (fermeture / fin d'édition) avant 4 s, sans toast d'erreur. */
export const SUBMIT_SEL = 'button[type="submit"], [data-testid$="-save"], [data-testid$="-submit"], [data-testid$="-confirm"], [data-testid*="save"]';
export const domSave = (id, getEl) => () => new Promise((res) => {
  const btn = getEl()?.querySelector(SUBMIT_SEL);
  if (!btn) { res(false); return; }
  const n = errCount(); const t0 = Date.now();
  btn.click();
  const poll = () => { if (!entries.has(id)) res(true); else if (errCount() > n || Date.now() - t0 > 4000) res(false); else setTimeout(poll, 100); };
  setTimeout(poll, 150);
});

if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") window.__deskLeave = { registerLeave, unregisterLeave, leaveEntries }; // preview : inspection / captures

/** iter446 — Précédent / Suivant du navigateur. Écouteur posé AVANT le routeur (index.js) : il voit l'événement le premier ; si le registre
 *  n'est pas vide, il empêche le routeur de réagir (stopImmediatePropagation), revient sur l'entrée d'origine (history.go) sans rechargement,
 *  puis ouvre la fenêtre commune ; « Quitter » reprend la navigation vers l'entrée cible. Le routeur garde l'index dans history.state.idx. */
let popBypass = 0;
export function installPopstateGuard() {
  if (typeof window === "undefined" || window.__deskPopGuard) return;
  window.__deskPopGuard = true;
  let idx = Number(window.history.state?.idx ?? 0);
  const sync = () => { idx = Number(window.history.state?.idx ?? idx); };
  for (const m of ["pushState", "replaceState"]) { const orig = window.history[m].bind(window.history); window.history[m] = (...a) => { const r = orig(...a); sync(); return r; }; }
  window.addEventListener("popstate", (e) => {
    const next = Number(window.history.state?.idx ?? idx);
    if (popBypass > 0) { popBypass -= 1; idx = next; return; }
    if (entries.size === 0 || next === idx) { idx = next; return; }
    const delta = idx - next; // retour vers l'entrée d'origine
    e.stopImmediatePropagation();
    popBypass += 1;
    window.history.go(delta);
    requestLeave("page", () => { popBypass += 1; window.history.go(-delta); setTimeout(() => { popBypass = 0; }, 800); });
  }, true);
}
